// ─────────────────────────────────────────────────────────────────────────────
// NYC Ferry on the map: the vessel mesh, how a boat moves between GPS fixes, and which of
// mesh / badge / heading arrow each display mode draws.
//
// Framework-free, like r160Model / xcel40Model, so all three are unit-tested.
//
// ── Why the boat is PROCEDURAL, when the train and bus are authored glTFs ──
// Those two bodies are authored in the sibling mta-assets repo and exported per LOD. There
// is no ferry there yet, and a boat is the one vehicle whose silhouette survives being
// built from a handful of extruded plan shapes: two demihulls, a cross-deck, a cabin, a
// wheelhouse. ~200 triangles, vertex-coloured, one SimpleMeshLayer — cheaper than the
// bus's lowest tier, which matters less than it would for trains since ~20 boats run at
// once. When an authored ferry lands in mta-assets it should replace this the way the
// Xcelsior replaced the bus's hand-built boxes; `FERRY_LENGTH_M` and the frame below are
// the contract it would need to keep.
//
// The frame is deck's own: +X forward (bow), +Y to port, +Z up, metres, origin on the
// waterline at mid-length. So the layer needs no roll (a glTF, exported Y-up, does).
// ─────────────────────────────────────────────────────────────────────────────

import type { VehicleDisplay } from '../../store/settings'

/** An Incat Crowther 85′ — most of the fleet; the 97′ boats are ~10% longer. */
export const FERRY_LENGTH_M = 26
export const FERRY_BEAM_M = 8.6

/**
 * The boat is drawn 2× life size. A bus is 1.5× so two on one avenue don't smear together;
 * a ferry has the whole harbour to itself and nothing to crowd, but at 1:1 it is 3 px at
 * zoom 14 over open water, which reads as a speck on the waves rather than a vessel. At 2×
 * it is ~52 m — still well under a pier's length, so it never overhangs its landing absurdly.
 */
export const FERRY_SIZE_SCALE = 2

/** Below this zoom `optimized` draws the badge + heading arrow instead of the mesh: at 13
 *  the drawn boat is ~5 px long. Between R160_MESH_MIN_ZOOM (15) and the bus's ladder,
 *  because a boat is a much bigger object than either. */
export const FERRY_MESH_MIN_ZOOM = 14

/** Where a badge hangs when it rides over the mesh (`both`): clear of the wheelhouse roof. */
export const FERRY_LABEL_ALT = 8 * FERRY_SIZE_SCALE

type RGB = readonly [number, number, number]
type XY = readonly [number, number]

// Linear-ish 0–1 vertex colours (SimpleMeshLayer multiplies them by the instance colour).
const WHITE: RGB = [0.95, 0.96, 0.97]
const ROOF: RGB = [0.84, 0.86, 0.88]
const DECK: RGB = [0.52, 0.55, 0.58]
const GLASS: RGB = [0.09, 0.13, 0.19]
/** NYC Ferry's navy, for the boot-top: the band of hull that meets the water. */
const NAVY: RGB = [0.11, 0.19, 0.33]
/** The stripe mesh is authored WHITE so its instance colour — the route's — comes through
 *  unmodified. */
const STRIPE: RGB = [1, 1, 1]

/** The attribute form SimpleMeshLayer's `mesh` prop takes directly (see its
 *  normalizeGeometryAttributes): non-indexed triangles, one colour per vertex. */
export interface FerryMesh {
  positions: { value: Float32Array; size: 3 }
  normals: { value: Float32Array; size: 3 }
  colors: { value: Float32Array; size: 3 }
}

class MeshBuilder {
  private pos: number[] = []
  private nrm: number[] = []
  private col: number[] = []

  private tri(a: readonly number[], b: readonly number[], c: readonly number[], n: readonly number[], color: RGB) {
    for (const v of [a, b, c]) {
      this.pos.push(v[0], v[1], v[2])
      this.nrm.push(n[0], n[1], n[2])
      this.col.push(color[0], color[1], color[2])
    }
  }

  /**
   * A convex plan polygon (counter-clockwise seen from above) extruded from z0 to z1: its
   * walls, and its top when `top` is given. No bottom — every one of these either sits on
   * something or on the water, and nothing on this map looks up at a boat.
   */
  prism(poly: readonly XY[], z0: number, z1: number, side: RGB, top?: RGB) {
    for (let i = 0; i < poly.length; i++) {
      const [x0, y0] = poly[i]
      const [x1, y1] = poly[(i + 1) % poly.length]
      const len = Math.hypot(x1 - x0, y1 - y0) || 1
      // Outward normal of a CCW edge is its direction turned clockwise.
      const n = [(y1 - y0) / len, -(x1 - x0) / len, 0]
      const a = [x0, y0, z0], b = [x1, y1, z0], c = [x1, y1, z1], d = [x0, y0, z1]
      this.tri(a, b, c, n, side)
      this.tri(a, c, d, n, side)
    }
    if (top) {
      const up = [0, 0, 1]
      for (let i = 1; i < poly.length - 1; i++) {
        this.tri([...poly[0], z1], [...poly[i], z1], [...poly[i + 1], z1], up, top)
      }
    }
  }

  build(): FerryMesh {
    return {
      positions: { value: new Float32Array(this.pos), size: 3 },
      normals: { value: new Float32Array(this.nrm), size: 3 },
      colors: { value: new Float32Array(this.col), size: 3 },
    }
  }
}

const HALF = FERRY_LENGTH_M / 2
const HULL_Y = 3.0 // demihull centreline, off the boat's
const HULL_HALF_W = 1.25
const HULL_TOP = 1.5
const BOOT_TOP = 0.55

/** One demihull's plan: flat transom aft, a knife bow. Counter-clockwise from above. */
function demihull(yc: number, grow = 0): XY[] {
  const w = HULL_HALF_W + grow
  return [
    [-HALF - grow, yc - w],
    [HALF - 4, yc - w],
    [HALF + grow, yc],
    [HALF - 4, yc + w],
    [-HALF - grow, yc + w],
  ]
}

/** The white-and-glass vessel. Route colour is not in it: see `buildFerryStripeMesh`. */
export function buildFerryHullMesh(): FerryMesh {
  const m = new MeshBuilder()
  for (const yc of [-HULL_Y, HULL_Y]) {
    m.prism(demihull(yc), 0, BOOT_TOP, NAVY)
    m.prism(demihull(yc), BOOT_TOP, HULL_TOP, WHITE)
  }
  // Cross-deck spanning the hulls, raked at the bow.
  const bw = FERRY_BEAM_M / 2
  m.prism(
    [[-HALF, -bw], [HALF - 5, -bw], [HALF - 2, -2.4], [HALF - 2, 2.4], [HALF - 5, bw], [-HALF, bw]],
    HULL_TOP, HULL_TOP + 0.4, WHITE, DECK,
  )
  // Main cabin: wall, window band, wall, roof. Set back from the transom for the aft deck.
  const main: XY[] = [[-HALF + 2, -bw + 0.3], [5, -bw + 0.3], [7.2, -2.4], [7.2, 2.4], [5, bw - 0.3], [-HALF + 2, bw - 0.3]]
  const deck = HULL_TOP + 0.4
  m.prism(main, deck, deck + 0.7, WHITE)
  m.prism(main, deck + 0.7, deck + 1.8, GLASS)
  m.prism(main, deck + 1.8, deck + 2.3, WHITE, ROOF)
  // Upper cabin + wheelhouse, forward of midships, glazed all round.
  const upper: XY[] = [[-4, -3], [3.4, -3], [4.8, -1.8], [4.8, 1.8], [3.4, 3], [-4, 3]]
  const roof = deck + 2.3
  m.prism(upper, roof, roof + 0.5, WHITE)
  m.prism(upper, roof + 0.5, roof + 1.5, GLASS)
  m.prism(upper, roof + 1.5, roof + 1.9, WHITE, WHITE)
  return m.build()
}

/**
 * A band round each demihull, drawn by its own layer in the ROUTE's colour.
 *
 * Not a tint: `getColor` on the hull layer would multiply the whole boat, glazing and all —
 * the thing the map's no-tint rule exists to stop (see transitMapGeometry). A separate mesh
 * coloured per instance paints only the band, the way a livery would. The fleet carries no
 * route colours in real life (they swap routes daily); this is the map telling you which
 * line a boat is running, in the one place on the hull that reads from a pitched camera.
 * Sits 3 cm proud of the hull so the depth test never hides it behind its own wall.
 */
export function buildFerryStripeMesh(): FerryMesh {
  const m = new MeshBuilder()
  for (const yc of [-HULL_Y, HULL_Y]) m.prism(demihull(yc, 0.03), 0.8, 1.15, STRIPE)
  return m.build()
}

// ── Display mode ─────────────────────────────────────────────────────────────

export interface FerryRenderMode {
  mesh: boolean
  badge: boolean
  /** The flat heading dart beside the badge — the zoomed-out stand-in for the bow. */
  arrow: boolean
}

/**
 * What a ferry draws under each of the rider's four display modes — the same meanings the
 * trains use (see VehicleDisplay in store/settings):
 *   labels    → the badge alone, never a mesh
 *   trains    → the mesh at every zoom
 *   both      → the mesh with the badge riding over it
 *   optimized → mesh from FERRY_MESH_MIN_ZOOM, badge + heading arrow below it
 */
export function ferryRenderMode(display: VehicleDisplay, zoom: number): FerryRenderMode {
  switch (display) {
    case 'labels':
      return { mesh: false, badge: true, arrow: false }
    case 'trains':
      return { mesh: true, badge: false, arrow: false }
    case 'both':
      return { mesh: true, badge: true, arrow: false }
    default:
      return zoom >= FERRY_MESH_MIN_ZOOM
        ? { mesh: true, badge: false, arrow: false }
        : { mesh: false, badge: true, arrow: true }
  }
}

// ── Motion between fixes ─────────────────────────────────────────────────────

/**
 * How a boat moves between the operator's GPS fixes.
 *
 * The bus's cousin, minus the road: a bus is snapped onto its route shape because it cannot
 * leave the street, but a boat is not on its drawn line — it swings wide off a pier and takes
 * whatever line harbour traffic allows. So this glides in a STRAIGHT line from where the
 * boat is drawn now to the newest fix, over one poll interval, and never snaps to a shape.
 *
 * A new fix always starts the next glide from the CURRENT drawn position, not from the old
 * fix, so a fix arriving early or late changes the speed, never the continuity.
 */
export interface FerryGlide {
  fromLon: number
  fromLat: number
  toLon: number
  toLat: number
  t0: number
  t1: number
  lon: number
  lat: number
  heading: number
  /** The fix this glide is heading for, so a repeated fix doesn't restart it. */
  fixKey: string
}

export interface FerryFix {
  lon: number
  lat: number
  /** Degrees clockwise from north; the backend derives it from successive fixes. */
  bearing: number
}

/** The poll interval (config FERRY_POLL_INTERVAL_MS): one glide spans one gap. */
export const FERRY_GLIDE_S = 30
/** Further than this from where it is drawn, a boat is re-placed rather than glided — a
 *  first sight after the app slept, or a vessel reassigned across the harbour. */
export const FERRY_SNAP_M = 2000
/** How quickly the bow comes round to a new heading. A boat turns slowly; seconds, not
 *  the frame-scale easing a train's model uses on a track tangent. */
export const FERRY_HEADING_TAU_S = 2.5

const M_PER_DEG = 111_320

function metresBetween(lon0: number, lat0: number, lon1: number, lat1: number): number {
  const kx = Math.cos((lat0 * Math.PI) / 180)
  return Math.hypot((lon1 - lon0) * kx, lat1 - lat0) * M_PER_DEG
}

/** Advance (or start) a boat's glide to `nowSec`, taking `fix` as the latest position.
 *  An existing glide is updated IN PLACE and returned; a missing one is created. */
export function advanceFerryGlide(
  g: FerryGlide | undefined,
  fix: FerryFix,
  nowSec: number,
  dtSec: number,
): FerryGlide {
  const key = `${fix.lat.toFixed(6)},${fix.lon.toFixed(6)}`
  const bearing = Number.isFinite(fix.bearing) ? fix.bearing : 0
  const placed = (): FerryGlide => ({
    fromLon: fix.lon, fromLat: fix.lat, toLon: fix.lon, toLat: fix.lat,
    t0: nowSec, t1: nowSec, lon: fix.lon, lat: fix.lat, heading: bearing, fixKey: key,
  })
  if (!g) return placed()
  if (key !== g.fixKey) {
    // Updated in place, like every other path here: callers may hold `g`.
    if (metresBetween(g.lon, g.lat, fix.lon, fix.lat) > FERRY_SNAP_M) return Object.assign(g, placed())
    g.fromLon = g.lon
    g.fromLat = g.lat
    g.toLon = fix.lon
    g.toLat = fix.lat
    g.t0 = nowSec
    g.t1 = nowSec + FERRY_GLIDE_S
    g.fixKey = key
  }
  const span = g.t1 - g.t0
  const p = span > 0 ? Math.min(1, Math.max(0, (nowSec - g.t0) / span)) : 1
  g.lon = g.fromLon + (g.toLon - g.fromLon) * p
  g.lat = g.fromLat + (g.toLat - g.fromLat) * p
  const k = dtSec > 0 ? 1 - Math.exp(-dtSec / FERRY_HEADING_TAU_S) : 0
  const turn = ((bearing - g.heading + 540) % 360) - 180 // shortest signed turn
  g.heading = (g.heading + turn * k + 360) % 360
  return g
}
